import type { TemplateEditorState } from '../templateEditor/types';
import type { ImportDataMode, ImportBehavior } from '@firesquirrel/local-db';

const DATA_MODES: { value: ImportDataMode; label: string; desc: string }[] = [
  { value: 'parcel',  label: 'Parcel / Entity', desc: 'Properties, buildings, parcels, sites' },
  { value: 'lookup',  label: 'Lookup Data',     desc: 'Code lists and reference tables' },
  { value: 'contact', label: 'Contacts',        desc: 'People and organization contacts' },
  { value: 'generic', label: 'Generic',         desc: 'Unclassified / custom data' },
  { value: 'parcelOwnerUpdate', label: 'Ownership Update', desc: 'Update-only owner refresh — never creates parcels; guarded by a source-freshness date' },
];

const BEHAVIORS: { value: ImportBehavior; label: string; desc: string }[] = [
  { value: 'upsert',    label: 'Upsert',      desc: 'Create new, update existing by match key' },
  { value: 'insert',    label: 'Insert Only', desc: 'Skip if match key already exists' },
  { value: 'append',    label: 'Append',      desc: 'Always insert; duplicates allowed' },
  { value: 'full-sync', label: 'Full Sync',   desc: 'Upsert + soft-delete absent records' },
];

// Note: vendorHint and expectedFileTypes were previously exposed on this tab
// but never consumed anywhere in the import pipeline — they were
// purely-decorative metadata that pushed configuration noise onto the user.
// File format (CSV vs XLSX vs GPKG vs …) is determined by the parser at
// runtime and does not need a user-declared hint; what matters is whether
// the source column names in the mapping are present in the file.
// The fields remain in the DB schema for backward compatibility, but the
// editor no longer exposes them.

export interface BasicsTabContentProps {
  state: TemplateEditorState;
  onChange: (patch: Partial<TemplateEditorState>) => void;
  errors?: string[];
}

export default function BasicsTabContent({ state, onChange, errors = [] }: BasicsTabContentProps) {
  return (
    <div className="space-y-6 max-w-xl">
      {errors.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 space-y-1">
          {errors.map(e => (
            <p key={e} className="text-xs text-red-700">• {e}</p>
          ))}
        </div>
      )}

      {/* Name */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">
          Template Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          data-testid="basics-name"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-hidden focus:ring-1 focus:ring-blue-500"
          placeholder="e.g. County Parcel Import"
          value={state.name}
          onChange={e => onChange({ name: e.target.value })}
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">Description</label>
        <textarea
          rows={3}
          data-testid="basics-description"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-hidden focus:ring-1 focus:ring-blue-500 resize-none"
          placeholder="Brief description of what this template imports"
          value={state.description}
          onChange={e => onChange({ description: e.target.value })}
        />
      </div>

      {/* Data Mode */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-2">
          Data Mode <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 gap-2">
          {DATA_MODES.map(m => (
            <button
              key={m.value}
              type="button"
              data-testid={`data-mode-${m.value}`}
              onClick={() => onChange({ dataMode: m.value })}
              className={[
                'border-2 rounded-lg px-4 py-3 text-left transition-colors',
                state.dataMode === m.value
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white',
              ].join(' ')}
            >
              <div className="text-sm font-semibold text-gray-800">{m.label}</div>
              <div className="text-[11px] text-gray-500 mt-0.5">{m.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Import Behavior — parcelOwnerUpdate is update-only by contract, so
          the selector collapses to a fixed note instead of offering
          insert-capable behaviors that can never run. */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-2">
          Default Import Behavior <span className="text-red-500">*</span>
        </label>
        {state.dataMode === 'parcelOwnerUpdate' ? (
          <div
            data-testid="behavior-owner-update-note"
            className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-sm p-3"
          >
            Ownership Update imports are <strong>update-only</strong>: matched parcels get
            their <code>ownership.*</code> fields refreshed, unmatched rows are skipped, and
            a per-parcel freshness check prevents stale files from overwriting newer data.
            Insert, append, and full-sync do not apply.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              {BEHAVIORS.map(b => (
                <button
                  key={b.value}
                  type="button"
                  data-testid={`behavior-${b.value}`}
                  onClick={() => onChange({ importBehavior: b.value })}
                  className={[
                    'border-2 rounded-lg px-3 py-2.5 text-left transition-colors',
                    state.importBehavior === b.value
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white',
                  ].join(' ')}
                >
                  <div className="text-xs font-semibold text-gray-800">{b.label}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">{b.desc}</div>
                </button>
              ))}
            </div>
            {state.importBehavior === 'full-sync' && (
              <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
                ⚠ Full Sync will soft-delete records not present in the uploaded file.
              </div>
            )}
          </>
        )}
      </div>

    </div>
  );
}
